import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import { CheckCircleFill, ExclamationTriangleFill, Trash } from 'react-bootstrap-icons';
import SchedulingIcon from '../img/schedule.gif';
import SchedulingGuideModal from '../Components/SchedulingGuideModal.jsx';
import '../CSS/Scheduling.css';

const localizer = momentLocalizer(moment);

function Scheduling() {
  const [events, setEvents] = useState(() => {
    const savedEvents = localStorage.getItem('events');
    if (savedEvents) {
      try {
        const parsedEvents = JSON.parse(savedEvents);
        return parsedEvents.map(event => ({
          ...event,
          start: new Date(event.start),
          end: new Date(event.end),
        }));
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  const [newEvent, setNewEvent] = useState({ title: '', start: '', end: '' });

  const [toast, setToast] = useState({ show: false, message: '', variant: '' });

  useEffect(() => {
    localStorage.setItem('events', JSON.stringify(events));
  }, [events]);

  // Auto-dismiss toast after 3 s
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    return () => clearTimeout(t);
  }, [toast.show]);

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
  };

  const handleSelectSlot = ({ start, end }) => {
    const title = window.prompt('Enter event title:');
    if (title) {
      setEvents(prev => [...prev, { start: new Date(start), end: new Date(end), title }]);
      showToast('Event added successfully');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewEvent(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const { title, start, end } = newEvent;
    if (!title || !start || !end) {
      showToast('Please fill in all fields.', 'danger');
      return;
    }
    const startDate = new Date(start);
    const endDate   = new Date(end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      showToast('Invalid date format', 'danger');
      return;
    }
    setEvents(prev => [...prev, { title, start: startDate, end: endDate }]);
    setNewEvent({ title: '', start: '', end: '' });
    showToast('Event added successfully');
  };

  const handleEventDelete = (eventToDelete) => {
    setEvents(prev =>
      prev.filter(ev =>
        ev.start.getTime() !== eventToDelete.start.getTime() ||
        ev.end.getTime()   !== eventToDelete.end.getTime()   ||
        ev.title           !== eventToDelete.title
      )
    );
    showToast('Event deleted successfully');
  };

  const eventPropGetter = (event) => ({
    style: {
      backgroundColor: event.bgColor || '#4facfe',
      color: 'white',
      cursor: 'pointer',
      borderRadius: '6px',
      border: 'none',
    },
  });

  const EventComponent = ({ event }) => (
    <span className="sch-event-label">
      <strong>{event.title}</strong>
      <button
        className="sch-event-delete-btn"
        onClick={(e) => { e.stopPropagation(); handleEventDelete(event); }}
        title="Delete event"
        aria-label="Delete event"
      >
        <Trash />
      </button>
    </span>
  );

  return (
    <div className="sch-page">

      {/* ── Toast ── */}
      <div className="sch-toast-region" aria-live="polite" aria-atomic="true">
        <div
          className={`sch-toast sch-toast-${toast.variant} ${toast.show ? 'sch-toast-show' : ''}`}
          role="alert"
        >
          <div className="sch-toast-header">
            <span className="sch-toast-title">
              {toast.variant === 'success' ? 'Success' : 'Error'}
            </span>
            <button
              className="sch-toast-close"
              onClick={() => setToast(p => ({ ...p, show: false }))}
              aria-label="Close"
            >×</button>
          </div>
          <div className="sch-toast-body">
            {toast.variant === 'success'
              ? <CheckCircleFill className="sch-toast-icon" />
              : <ExclamationTriangleFill className="sch-toast-icon" />}
            {toast.message}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="sch-inner-container">

        {/* Page heading */}
        <div className="row">
          <div className="col-12">
            <h3>
              Scheduling
              <img className="sch-animated-icon" src={SchedulingIcon} alt="Scheduling icon" />
            </h3>
          </div>
        </div>

        {/* Calendar + Form */}
        <div className="sch-body-row">

          {/* Calendar */}
          <div className="sch-calendar-col">
            <div className="sch-calendar-scroll">
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 500 }}
                onSelectSlot={handleSelectSlot}
                selectable
                eventPropGetter={eventPropGetter}
                components={{ event: EventComponent }}
                defaultView="week"
                views={['month', 'week', 'day']}
              />
            </div>
          </div>

          {/* Add Event form */}
          <div className="sch-form-col">
            <h4 className="sch-form-title">Add Event</h4>

            <div className="sch-form">

              <div className="sch-form-field">
                <input
                  type="text"
                  className="sch-input"
                  name="title"
                  value={newEvent.title}
                  onChange={handleFormChange}
                  placeholder=" "
                  id="sch-title"
                />
                <label className="sch-label" htmlFor="sch-title">Title</label>
              </div>

              <div className="sch-form-field">
                <input
                  type="datetime-local"
                  className="sch-input"
                  name="start"
                  value={newEvent.start}
                  onChange={handleFormChange}
                  placeholder=" "
                  id="sch-start"
                />
                <label className="sch-label" htmlFor="sch-start">Start Date</label>
              </div>

              <div className="sch-form-field">
                <input
                  type="datetime-local"
                  className="sch-input"
                  name="end"
                  value={newEvent.end}
                  onChange={handleFormChange}
                  placeholder=" "
                  id="sch-end"
                />
                <label className="sch-label" htmlFor="sch-end">End Date</label>
              </div>

              <button
                type="button"
                className="sch-btn sch-btn-success"
                onClick={handleFormSubmit}
              >
                Add Event
              </button>

            </div>
          </div>

        </div>
      </div>
        <SchedulingGuideModal />
    </div>
  );
}

export default Scheduling;